import { PROVIDER_LEVELS } from '@ems/clinical';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { BackendNotConfigured } from '@/components/guards';
import { Alert, Button, Field, Input, Segmented, Select, Spinner } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { authClient, db } from '@/lib/db';
import { APP_NAME, isBackendConfigured } from '@/lib/env';
import { errorMessage } from '@/lib/utils';

/** Only allow same-origin relative redirects after sign-in. */
function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** Better Auth returns `{ data, error }`; surface the error message or pass data through. */
function check<T>(result: { data: T; error: { message?: string; status?: number } | null }): T {
  if (result.error) throw new Error(result.error.message || `Request failed (${result.error.status ?? 'network'})`);
  return result.data;
}

function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-8 flex items-center gap-2">
          <img src="/icons/icon.svg" alt="" className="size-8 rounded-[8px] dark:ring-1 dark:ring-white/15" />
          <span className="text-lg font-semibold tracking-tight">{APP_NAME}</span>
        </Link>
        <div className="rounded-md border border-stone-300 bg-white p-6 dark:border-stone-700 dark:bg-ink-900">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-stone-500">{subtitle}</p>}
          <div className="mt-5">{isBackendConfigured ? children : <BackendNotConfigured />}</div>
        </div>
        {footer && <div className="mt-4 text-sm text-stone-600 dark:text-stone-400">{footer}</div>}
        <div className="mt-8 border-t border-stone-200 pt-4 dark:border-stone-800">
          <Link to="/tools" className="text-sm text-stone-500 hover:text-stone-950 dark:text-stone-400 dark:hover:text-white">
            Use the clinical tools without signing in →
          </Link>
        </div>
      </div>
    </div>
  );
}

function useFinishSignIn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refresh } = useAuth();
  return async (next: string) => {
    queryClient.clear();
    await refresh();
    navigate(next, { replace: true });
  };
}

export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const { user, ready, offlineSession } = useAuth();
  const finish = useFinishSignIn();
  const [mode, setMode] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (ready && user && !offlineSession) return <Navigate to={next} replace />;

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (mode === 'password') {
        check(await authClient.signIn.email({ email, password }));
        await finish(next);
      } else if (!codeSent) {
        check(await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' }));
        setCodeSent(true);
      } else {
        check(await authClient.signIn.emailOtp({ email, otp: otp.trim() }));
        await finish(next);
      }
    });
  };

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          New here?{' '}
          <Link to={`/signup${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-stone-950 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-950 dark:text-white dark:decoration-stone-600 dark:hover:decoration-white">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Segmented
          className="w-full"
          value={mode}
          onChange={(m) => {
            setMode(m);
            setCodeSent(false);
            setError(null);
          }}
          options={[
            { value: 'password', label: 'Password' },
            { value: 'code', label: 'Email code' },
          ]}
        />
        <Field label="Email">
          <Input type="email" autoComplete="email" required value={email} disabled={codeSent} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {mode === 'password' && (
          <Field label="Password">
            <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        )}
        {mode === 'code' && codeSent && (
          <Field label="6-digit code" hint={`Sent to ${email}. Codes expire after a few minutes.`}>
            <Input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" required value={otp} onChange={(e) => setOtp(e.target.value)} className="text-center text-lg tracking-[0.4em]" />
          </Field>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" size="lg" className="w-full" loading={loading}>
          {mode === 'password' ? 'Sign in' : codeSent ? 'Verify code' : 'Email me a code'}
        </Button>
        <div className="flex justify-between text-sm">
          {mode === 'code' && codeSent ? (
            <button type="button" className="font-medium text-stone-950 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-950 dark:text-white dark:decoration-stone-600 dark:hover:decoration-white" onClick={() => setCodeSent(false)}>
              Use a different email
            </button>
          ) : (
            <span />
          )}
          <Link to="/forgot-password" className="text-stone-500">
            Forgot password?
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function SignupPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const finish = useFinishSignIn();
  const [form, setForm] = useState({ fullName: '', email: '', password: '', level: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = check(await authClient.signUp.email({ email: form.email, password: form.password, name: form.fullName.trim() }));
      // The database creates the profile from neon_auth."user"; add what Better Auth doesn't collect.
      if (form.level && result?.user?.id) {
        const { error: profileError } = await db.from('profiles').update({ certification_level: form.level }).eq('id', result.user.id);
        if (profileError) console.warn('Could not save certification level', profileError.message);
      }
      await finish(next);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Use your work email if your agency invited you."
      footer={
        <>
          Already registered?{' '}
          <Link to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-stone-950 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-950 dark:text-white dark:decoration-stone-600 dark:hover:decoration-white">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name">
          <Input autoComplete="name" required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        </Field>
        <Field label="Email">
          <Input type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Password" hint="At least 8 characters">
          <Input type="password" autoComplete="new-password" minLength={8} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        <Field label="Certification level">
          <Select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
            <option value="">Select…</option>
            {PROVIDER_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </Select>
        </Field>
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" size="lg" className="w-full" loading={loading}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Password reset with an emailed one-time code (stays inside the installed app). */
export function ForgotPasswordPage() {
  const finish = useFinishSignIn();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (step === 'email') {
        check(await authClient.emailOtp.requestPasswordReset({ email }));
        setStep('reset');
      } else {
        check(await authClient.emailOtp.resetPassword({ email, otp: otp.trim(), password }));
        check(await authClient.signIn.email({ email, password }));
        await finish('/');
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Reset password" footer={<Link to="/login" className="font-medium text-stone-950 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-950 dark:text-white dark:decoration-stone-600 dark:hover:decoration-white">Back to sign in</Link>}>
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Email">
          <Input type="email" autoComplete="email" required value={email} disabled={step === 'reset'} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {step === 'reset' && (
          <>
            <Alert tone="info">If an account exists for {email}, we sent a 6-digit code.</Alert>
            <Field label="Code">
              <Input inputMode="numeric" autoComplete="one-time-code" required value={otp} onChange={(e) => setOtp(e.target.value)} className="text-center text-lg tracking-[0.4em]" />
            </Field>
            <Field label="New password" hint="At least 8 characters">
              <Input type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          </>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" className="w-full" loading={loading}>
          {step === 'email' ? 'Send reset code' : 'Update password'}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Return point for OAuth and email-link flows; Managed Better Auth has already set the session. */
export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const finish = useFinishSignIn();
  const error = params.get('error_description') ?? params.get('error');
  useEffect(() => {
    if (!error) void finish(safeNext(params.get('next')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <AuthLayout title="Signing you in">{error ? <Alert tone="danger">{error}</Alert> : <Spinner label="Verifying" />}</AuthLayout>;
}
